import Modal from "@components/atoms/Modal";
import styled from "styled-components/native";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { ScrollView } from "react-native";
import { useMemo, useState } from "react";
import { AppliedCoupon, computePlanPrice, formatToday, Plan } from "./utils";
import {
  useCreateContract,
  useUpdateContract,
  useUpdateRenewalContract,
} from "@apis/billing";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useGlobalData } from "@redux/slices/globalSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ReviewChangesPopup from "./components/ReviewChangesPopUp";
import { useGetUser } from "@apis/user";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import ManagePlanStep1 from "./ManagePlanStep1";
import ManagePlanStep2 from "./ManagePlanStep2";
import { VDBIcons } from "@utils/VDBIcons";

const ManagePlanComponent = ({
  onClose,
  setShowSuccessPopup,
  step,
  setStep,
  appliedCoupon,
  setAppliedCoupon,
  initialManagePlanProps,
  setShowCancelRenewalPopup,
}: {
  onClose: () => void;
  setShowSuccessPopup: (show: boolean) => void;
  step: number;
  setStep: (step: number) => void;
  appliedCoupon: AppliedCoupon;
  setAppliedCoupon: (coupon: AppliedCoupon) => void;
  initialManagePlanProps: {
    type: "renewal" | "purchase";
    plan_id: string;
    additional_users: number;
  };
  setShowCancelRenewalPopup: (show: boolean) => void;
}) => {
  const {
    user,
    isUpcomingNotPaidTrialPlan,
    getUpcomingSubscription,
    isUpcomingNotPaidPlan,
    isMembershipExpired,
  } = useUserInfo();
  const today = formatToday();
  const globalData = useGlobalData();
  const plansData = globalData?.subscription_config;
  const isModifyPlan =
    isUpcomingNotPaidTrialPlan() ||
    (isUpcomingNotPaidPlan() && isMembershipExpired());
  const currentPlan = user?.plan_details?.plan;
  const upcomingPlan = getUpcomingSubscription();
  const upcomingPlanFromPlansData = plansData?.plans.find(
    (plan: Plan) => plan?.plan_type === upcomingPlan?.plan_duration
  );
  const getSelectedPlan = () => {
    if (isModifyPlan) {
      return plansData?.plans.find(
        (plan: Plan) => plan?.plan_type === currentPlan?.plan_duration
      );
    } else {
      return null;
    }
  };

  const getAddedAdditionalUsers = () => {
    const pricings = user?.plan_details?.zenskar_pricings;

    if (!isModifyPlan || !Array.isArray(pricings)) return 0;

    const item = pricings.find((p) => Array.isArray(p?.pricing_quantity));

    if (!item) return 0;

    // Return total count of pricing_quantity user_count items
    return item.pricing_quantity.reduce(
      (sum, q) => sum + (Number(q.user_count) || 0),
      0
    );
  };
  const { t, theme, typography } = useScreenOptionsHooks();
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(
    isModifyPlan
      ? plansData?.plans.findIndex(
          (plan: Plan) => plan.plan_id === getSelectedPlan()?.plan_id
        )
      : initialManagePlanProps.type === "renewal"
      ? plansData?.plans.findIndex(
          (plan: Plan) => plan.plan_id === initialManagePlanProps.plan_id
        )
      : null
  );
  const selectedPlan =
    selectedPlanIndex !== null ? plansData?.plans[selectedPlanIndex] : null;
  const [additionalUsers, setAdditionalUsers] = useState<number>(
    initialManagePlanProps.additional_users ?? getAddedAdditionalUsers() ?? 0
  );
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [showReviewChangesPopup, setShowReviewChangesPopup] = useState(false);
  const disableReviewButton =
    (additionalUsers === getAddedAdditionalUsers() &&
      selectedPlan?.plan_id === getSelectedPlan()?.plan_id) ||
    (initialManagePlanProps.type === "renewal" &&
      selectedPlan &&
      upcomingPlanFromPlansData &&
      selectedPlan?.plan_id === upcomingPlanFromPlansData?.plan_id &&
      additionalUsers === initialManagePlanProps.additional_users);
  const createContractMutation = isModifyPlan
    ? useUpdateContract()
    : initialManagePlanProps.type === "renewal" && upcomingPlan?.subscription_id
    ? useUpdateRenewalContract(upcomingPlan?.subscription_id)
    : useCreateContract();

  const [userId, setUserId] = useState(user.id);
  const {} = useGetUser(userId, setUserId);
  const { trackButtonEvent } = useUserAnalytics();
  const handleSelectPlan = (index: number) => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      index === 0
        ? EventType.PLAN_1_SELECTED_BUTTON_CLICK
        : EventType.PLAN_2_SELECTED_BUTTON_CLICK,
      { component_type: ViewType.POP_UP },
      ViewTypeEnum?.MANAGE_PLAN_POP_UP
    );
    setSelectedPlanIndex(index);
    setAppliedCoupon(null);
  };
  const handleReviewChangesPopup = () => {
    setShowReviewChangesPopup((prev: boolean) => !prev);
  };
  const totals = useMemo(
    () =>
      computePlanPrice(
        selectedPlan,
        additionalUsers,
        user?.is_tax_applicable,
        appliedCoupon
      ),
    [selectedPlan, additionalUsers, appliedCoupon]
  );
  const lines = useMemo(() => {
    const feeLabel = `${t(
      "managePlan.popUp.creditCardFee.text",
      "Processing Fee"
    )} (${selectedPlan?.credit_card_fee_percent ?? 0}%)`;
    const taxLabel = `${t("managePlan.popUp.taxes.text", "Taxes")} (${
      selectedPlan?.tax_percent ?? 0
    }%)`;
    return [
      {
        label: `${t("managePlan.popUp.basePlan.text", "Base Fee")}: ${
          selectedPlan?.label
        }`,
        value: totals?.planBase,
      },
      {
        label: `${t(
          "managePlan.popUp.additionalUsers.text",
          "Additional Users"
        )}: ${selectedPlan?.label} (${additionalUsers})`,
        value: totals?.addUsersCost,
      },
      ...(appliedCoupon?.coupon_code && (totals?.couponDiscount ?? 0) > 0
        ? [
            {
              label:
                appliedCoupon.discount_type === "percent"
                  ? `${t(
                      "managePlan.popUp.coupon.discount.text",
                      "Coupon discount"
                    )} (${appliedCoupon.discount_value}%)`
                  : t(
                      "managePlan.popUp.coupon.discount.text",
                      "Coupon discount"
                    ),
              value: -(totals?.couponDiscount ?? 0),
              bold: true,
            },
          ]
        : []),
      ...(user?.is_tax_applicable
        ? [{ label: taxLabel, value: totals?.tax }]
        : []),
      {
        label: t("managePlan.popUp.subtotal.text", "Sub Total"),
        value: totals?.taxedTotal,
      },
      { label: feeLabel, value: totals?.fee },
    ];
  }, [
    selectedPlan,
    totals,
    additionalUsers,
    user?.is_tax_applicable,
    appliedCoupon,
  ]);

  const handleCreateContract = (
    type: "confirm_plan" | "modify_plan" | "renewal"
  ) => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      EventType.CONFIRM_PLAN_BUTTON_CLICK,
      { component_type: ViewType.POP_UP },
      ViewTypeEnum?.MANAGE_PLAN_POP_UP
    );
    const couponCodeFor = (productId: any) =>
      appliedCoupon?.coupon_code &&
      (appliedCoupon?.applicable_product_ids || [])
        .map(String)
        .includes(String(productId))
        ? { coupon_code: appliedCoupon.coupon_code }
        : {};

    if (type === "renewal" && upcomingPlan && !upcomingPlan?.subscription_id) {
      showToast(
        undefined,
        t(
          "managePlan.popUp.renewal.previousRequestInProgress.error.text",
          "Previous request may be in progress. Please try again in sometime."
        ),
        ToastTypes.ERROR,
        true,
        true
      );
      setUserId(user.id);
      return;
    }

    const payload:
      | { plan_type: string; pricings: any[] }
      | { product_id: string; paid_users: number } =
      type === "renewal" && upcomingPlan?.subscription_id
        ? {
            plan_type: selectedPlan?.plan_type,
            pricings: [
              {
                product_id: selectedPlan?.plan_id,
                ...couponCodeFor(selectedPlan?.plan_id),
              },
              ...(additionalUsers > 0
                ? [
                    {
                      product_id: selectedPlan?.additional_user_plan?.id,
                      pricing_quantity: [
                        {
                          date: today,
                          user_count: additionalUsers,
                        },
                      ],
                      ...couponCodeFor(selectedPlan?.additional_user_plan?.id),
                    },
                  ]
                : []),
            ],
          }
        : type === "confirm_plan"
        ? {
            plan_type: selectedPlan?.plan_type,
            ...(user.is_tax_applicable && { taxes: selectedPlan?.tax_percent }),
            pricings: [
              {
                product_id: selectedPlan?.plan_id,
                pricing_id: selectedPlan?.pricing_id,
                amount: selectedPlan?.base_price,
                ...couponCodeFor(selectedPlan?.plan_id),
              },
              ...(additionalUsers > 0
                ? [
                    {
                      product_id: selectedPlan?.additional_user_plan?.id,
                      pricing_id:
                        selectedPlan?.additional_user_plan?.pricing_id,
                      pricing_quantity: additionalUsers,
                      amount: selectedPlan?.additional_user_plan?.base_price,
                      ...couponCodeFor(selectedPlan?.additional_user_plan?.id),
                    },
                  ]
                : []),
            ],
          }
        : {
            plan_type: selectedPlan?.plan_type,
            pricings: [
              {
                product_id: selectedPlan?.plan_id,
                ...couponCodeFor(selectedPlan?.plan_id),
              },
              ...(additionalUsers > 0
                ? [
                    {
                      product_id: selectedPlan?.additional_user_plan?.id,
                      pricing_quantity: [
                        {
                          date: today,
                          user_count: additionalUsers,
                        },
                      ],
                      ...couponCodeFor(selectedPlan?.additional_user_plan?.id),
                    },
                  ]
                : []),
            ],
          };
    createContractMutation.mutate(payload as any, {
      onSuccess: (data: any) => {
        if (data.data.status === "skipped") {
          showToast(undefined, data.data.message, ToastTypes.ERROR, true, true);
          setUserId(user.id);
          setTimeout(() => {
            setStep(1);
            setAppliedCoupon(null);
            onClose();
          }, 4000);
        } else {
          if (
            initialManagePlanProps.type === "renewal" &&
            upcomingPlan?.subscription_id
          ) {
            showToast(
              undefined,
              data.data.message,
              ToastTypes.SUCCESS,
              true,
              true
            );
            setTimeout(() => {
              setStep(1);
              setAppliedCoupon(null);
              setUserId(user.id);
              onClose();
            }, 4000);
          } else {
            setShowSuccessPopup(true);
            setStep(1);
            setAppliedCoupon(null);
            onClose();
          }
        }
      },
      onError: (error: any) => {
        showToast(undefined, error.data.message, ToastTypes.ERROR, true, true);
      },
    });
  };
  return (
    <>
      <ScrollView
        scrollEnabled={theme.isMobile ? true : false}
        showsVerticalScrollIndicator={false}
      >
        <Overlay>
          {step === 1 && (
            <ManagePlanStep1
              plansData={plansData}
              selectedPlanIndex={selectedPlanIndex}
              handleSelectPlan={handleSelectPlan}
              additionalUsers={additionalUsers}
              setAdditionalUsers={setAdditionalUsers}
              selectedPlan={selectedPlan}
              t={t}
              typography={typography}
              theme={theme}
              trackButtonEvent={trackButtonEvent}
              setShowContactUsModal={setShowContactUsModal}
              setShowCancelRenewalPopup={(show: boolean) => {
                setStep(1);
                setShowCancelRenewalPopup(show);
              }}
              showContactUsModal={showContactUsModal}
              setStep={setStep}
              disableReviewButton={disableReviewButton}
              type={initialManagePlanProps.type}
              setUserId={setUserId}
            />
          )}
          {step === 2 && (
            <ManagePlanStep2
              lines={lines}
              totals={totals}
              t={t}
              typography={typography}
              theme={theme}
              trackButtonEvent={trackButtonEvent}
              setShowContactUsModal={setShowContactUsModal}
              showContactUsModal={showContactUsModal}
              handleCreateContract={handleCreateContract}
              createContractMutation={createContractMutation}
              disableReviewButton={disableReviewButton}
              handleReviewChangesPopup={handleReviewChangesPopup}
              setShowCancelRenewalPopup={(show: boolean) => {
                setStep(1);
                setShowCancelRenewalPopup(show);
              }}
              isModifyPlan={isModifyPlan}
              productIds={[
                selectedPlan?.plan_id,
                ...(additionalUsers > 0
                  ? [selectedPlan?.additional_user_plan?.id]
                  : []),
              ].filter(Boolean)}
              appliedCoupon={appliedCoupon}
              setAppliedCoupon={setAppliedCoupon}
              hideCouponCode={selectedPlan?.hideCouponCode}
              type={initialManagePlanProps.type}
              setUserId={setUserId}
            />
          )}
        </Overlay>
      </ScrollView>
      <ReviewChangesPopup
        isOpen={showReviewChangesPopup}
        onClose={handleReviewChangesPopup}
        handleCreateContract={handleCreateContract}
        selectedPlan={getSelectedPlan()}
        additionalUsers={getAddedAdditionalUsers()}
        updatedPlan={selectedPlan}
        updatedAdditionalUsers={additionalUsers}
        lines={lines}
        totals={totals}
        isLoading={createContractMutation.isPending}
      />
    </>
  );
};

const ManagePlanPopUp = ({
  isOpen,
  onClose,
  setShowSuccessPopup,
  initialManagePlanProps,
  setShowCancelRenewalPopup,
}: {
  isOpen: boolean;
  onClose: () => void;
  setShowSuccessPopup: (show: boolean) => void;
  initialManagePlanProps: {
    type: "renewal" | "purchase";
    plan_id: string;
    additional_users: number;
  };
  setShowCancelRenewalPopup: (show: boolean) => void;
}) => {
  const { t, theme } = useScreenOptionsHooks();
  const {
    isUpcomingNotPaidTrialPlan,
    isUpcomingNotPaidPlan,
    isMembershipExpired,
  } = useUserInfo();
  const isModifyPlan =
    isUpcomingNotPaidTrialPlan() ||
    (isUpcomingNotPaidPlan() && isMembershipExpired());
  const [selectedStep, setSelectedStep] = useState<number>(1);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon>(null);
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setSelectedStep(1);
        setAppliedCoupon(null);
      }}
      paddingLeft={0}
      paddingRight={0}
      paddingBottom={0}
      paddingTop={0}
      headerTitle={
        initialManagePlanProps.type === "renewal"
          ? t("manageRenewal.popUp.title.text", "Manage Renewal")
          : isModifyPlan
          ? t("modifyPlan.popUp.title.text", "Modify Plan")
          : t("managePlan.popUp.title.text", "Manage Plan")
      }
      enableToastMessage={true}
      type={theme.isLargeScreen ? "popUp" : "sheet"}
      {...(theme.isLargeScreen ? { widthPercent: 54 } : { size: "lg" })}
      noPadding
      showScrollbar={false}
      skipKeyboardAwareWeb={true}
      {...(selectedStep === 2 && {
        leftIcon: VDBIcons.LEFT,
        leftIconPress: () => {
          setSelectedStep(1);
          setAppliedCoupon(null);
        },
      })}
    >
      <ManagePlanComponent
        onClose={onClose}
        setShowSuccessPopup={setShowSuccessPopup}
        step={selectedStep}
        setStep={setSelectedStep}
        appliedCoupon={appliedCoupon}
        setAppliedCoupon={setAppliedCoupon}
        initialManagePlanProps={initialManagePlanProps}
        setShowCancelRenewalPopup={setShowCancelRenewalPopup}
      />
    </Modal>
  );
};

const Overlay = styled.View`
  flex-direction: ${({ theme }: { theme: any }) =>
    theme.isMobile ? "column" : "column"};
`;

export default ManagePlanPopUp;
